import type React from 'react'
import { MetroStationHeader } from '@metro/station-details'
import { StatusBanner, type StatusBannerVariant } from './StatusBanner'
import { defaultStationCardCopy, type StationCardCopySet } from './copy'
import styles from './StationCardView.module.css'

export interface StationCardViewStation {
  id: string
  name: string
  lineLocalName: string
  lineHexColor: string
  zone: string | null
  /** `/<locale>/lines/<slug>` — the line link under the name; absent → plain text. */
  lineHref?: string
}

export interface StationCardViewProps {
  station: StationCardViewStation
  onFromTap: (stationId: string) => void
  onToTap: (stationId: string) => void
  onDetailsTap: () => void
  /**
   * Gates the From/To primary action row. Defaults to `true` so the View
   * keeps its iOS-parity layout in isolated tests; production callers
   * thread the `routeBuilder` feature flag through, hiding the buttons
   * when the flag is off. The "Station information" secondary button is
   * unaffected.
   */
  showRouteButtons?: boolean
  /**
   * Optional status overlay (spec §4 / Figma §2.4 / §2.5 / §2.6). When
   * set, renders the card as a sandwich variant:
   *   - `'closed'` — From/To disabled (opacity 0.4, `pointer-events: none`).
   *   - `'restricted'` — From/To remain interactive (limited service ≠ closure).
   *   - `'advisory'` — soft banner; From/To remain interactive.
   *
   * The banner container uses `display:none/block` toggling (no max-height
   * transition — SwiftShader mispaints clipped elements on initial mount).
   * In-place animation (spec §6) is handled by the inner StatusBanner child.
   */
  status?: StatusBannerVariant
  /** Fires when the status banner is tapped. Optional — no-op default. */
  onStatusBannerTap?: (stationId: string) => void
  /**
   * Opens the line's view on the map from the line link under the station
   * name (Figma LineView E). Only cards with a `lineHref` show the link.
   */
  onLineTap?: (stationId: string) => void
  /** Localised copy. @defaultValue {@link defaultStationCardCopy} */
  copy?: StationCardCopySet
}

/**
 * Single station card. Pixel-parity with Figma spec
 * `metro/docs/superpowers/specs/2026-05-20-stationcard-v2-figma-pixelperfect.md`.
 *
 * Non-sandwich layout (Normal / Long name / No zone):
 *
 *   VStack(gap: 12) {
 *     StationHeader(...)
 *     HStack(gap: 4) { fromButton; toButton }   // 52px, PrimaryButton r=18
 *     detailsButton                              // 64px, SecondaryButton r=32
 *   }
 *   .padding(16)
 *   .background(RoundedRectangle(cornerRadius: 32).fill(.backgroundPrimary))
 *   .shadow(rgba(0,0,0,0.2), radius: 8)
 *
 * Sandwich layout (Closed / Restricted, compact 2026-07): wrapper
 * `position: relative` with StatusBanner overlay at top:0 height:100 and the
 * opaque card body HUGGING its content, pinned to the wrapper BOTTOM. The body
 * sits ABOVE the banner (z-index) so it paints over the banner's bottom overlap
 * and the single-line title clears it (mirrors Figma, where the card body is
 * drawn after the banner). Banner peeks above (72 Closed / 64 Restricted).
 * Shadow blur jumps to 16 to match Figma's heavier sandwich drop-shadow.
 */
function statusBannerTitle(variant: StatusBannerVariant, copy: StationCardCopySet): string {
  if (variant === 'closed') return copy.statusClosedTitle
  if (variant === 'restricted') return copy.statusRestrictedTitle
  return copy.statusAdvisoryTitle
}

function statusBannerSubtitle(variant: StatusBannerVariant, copy: StationCardCopySet): string {
  if (variant === 'closed') return copy.statusClosedSubtitle
  if (variant === 'restricted') return copy.statusRestrictedSubtitle
  return copy.statusAdvisorySubtitle
}

export function StationCardView({
  station,
  onFromTap,
  onToTap,
  onDetailsTap,
  showRouteButtons = true,
  status,
  onStatusBannerTap,
  onLineTap,
  copy = defaultStationCardCopy,
}: StationCardViewProps): React.ReactElement {
  // Only 'closed' disables From/To; 'restricted' and 'advisory' leave them interactive.
  const fromToDisabled = status === 'closed'

  return (
    <div className={styles.wrapper} data-status={status} data-testid={`station-card-${station.id}`}>
      {/*
       * Banner container — display:none/block toggle (spec §6 in-place
       * animation requirement is met by opacity+translate on the inner
       * <StatusBanner> child). The container is hidden when no status so the
       * card body paints at top:0 (normal layout); shown when status is set,
       * which shifts the card body down via the [data-status] CSS rule.
       */}
      <div
        className={`${styles.bannerContainer} ${status !== undefined ? styles.bannerVisible : ''}`}
        aria-hidden={status === undefined}
      >
        {status !== undefined && (
          <StatusBanner
            variant={status}
            title={statusBannerTitle(status, copy)}
            subtitle={statusBannerSubtitle(status, copy)}
            onTap={
              onStatusBannerTap === undefined ? undefined : () => onStatusBannerTap(station.id)
            }
          />
        )}
      </div>
      <article className={styles.card}>
        <MetroStationHeader
          title={station.name}
          subtitle={station.lineLocalName}
          badge={station.zone}
          lineHexColor={station.lineHexColor}
          singleLineTitle
          subtitleHref={station.lineHref}
          onSubtitleTap={onLineTap === undefined ? undefined : () => onLineTap(station.id)}
        />
        <div className={styles.actionFooter}>
          {showRouteButtons && (
            <div className={styles.actionButtons}>
              <button
                type="button"
                className={styles.primaryButton}
                data-testid="station-card-from-button"
                onClick={() => onFromTap(station.id)}
                disabled={fromToDisabled}
                aria-disabled={fromToDisabled || undefined}
              >
                {copy.from}
              </button>
              <button
                type="button"
                className={styles.primaryButton}
                data-testid="station-card-to-button"
                onClick={() => onToTap(station.id)}
                disabled={fromToDisabled}
                aria-disabled={fromToDisabled || undefined}
              >
                {copy.to}
              </button>
            </div>
          )}
          <button
            type="button"
            className={styles.secondaryButton}
            data-testid="station-card-details-button"
            onClick={onDetailsTap}
          >
            {copy.stationInformation}
          </button>
        </div>
      </article>
    </div>
  )
}
